import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';

//components
import withLocalization from '~/hoc/withLocalization';
import Button from '../../../../components/CustomButton/CustomButton';

//elements
import Select from '~/elements/Select.js';
import SwitchWidget from '../../../Admin/ControlPanel/SwitchWidget';

//utils
import { payrollIntegrationOptions, getWeekInCycleOptions, TRIPLETEX_PLANS } from '../../../../utils/constants';

class IntegrationSettings extends Component {
    constructor(props) {
        super(props);
        this.syncDataLockPeriodOptions = getWeekInCycleOptions(props.t, 4);
        this.state = {
            isSaving: false,
            client_id: this.props.clientStore.currentEntity.client.id,
            payrollProduct: this.props.clientStore.currentEntity?.client?.integration_details?.product,
            packageName: this.props.clientStore.currentEntity?.client?.integration_details?.integration_settings?.packageName || null,
            integrationSettings: {
                syncTimesheetForHourlyPaidEmp: this.props.clientStore.currentEntity?.client?.integration_details?.integration_settings?.syncTimesheetForHourlyPaidEmp || false,
                syncTimesheetForFixedPaidEmp: this.props.clientStore.currentEntity?.client?.integration_details?.integration_settings?.syncTimesheetForFixedPaidEmp || false,
                syncTimesheetSupplementsForHourlyPaidEmp: this.props.clientStore.currentEntity?.client?.integration_details?.integration_settings?.syncTimesheetSupplementsForHourlyPaidEmp || false,
                syncTimesheetSupplementsForFixedPaidEmp: this.props.clientStore.currentEntity?.client?.integration_details?.integration_settings?.syncTimesheetSupplementsForFixedPaidEmp || false,
                syncAdditionalPaymentsWithAbsence: this.props.clientStore.currentEntity?.client?.integration_details?.integration_settings?.syncAdditionalPaymentsWithAbsence || false,
                sendAbsenceHoursInPercentage: this.props.clientStore.currentEntity?.client?.integration_details?.integration_settings?.sendAbsenceHoursInPercentage || false,
                allowAutoImportEntity: this.props.clientStore.currentEntity?.client?.integration_details?.integration_settings?.allowAutoImportEntity || false,
                allowLockingSyncedData: this.props.clientStore.currentEntity?.client?.integration_details?.integration_settings?.allowLockingSyncedData || false,
                syncedDataLockPeriodInWeeks: this.props.clientStore.currentEntity?.client?.integration_details?.integration_settings?.syncedDataLockPeriodInWeeks || null,
                useGeneralActivitiesForTimesheet: this.props.clientStore.currentEntity?.client?.integration_details?.integration_settings?.useGeneralActivitiesForTimesheet || false,
            }
        }
    }

    handleUserDataChange = (name, value) => {
        const { currentEntity } = this.props.clientStore;
        if (currentEntity.client.data.dateTimeRules) {
            currentEntity.client.data.dateTimeRules[name] = value;
        } else {
            let dateTimeRules = {};
            dateTimeRules[name] = value;
            currentEntity.client.data.dateTimeRules = dateTimeRules;
        }
    };

    handleIntegrationChange = (key, value) => {
        const { currentEntity } = this.props.clientStore;
        if (currentEntity.client?.integration_details?.integration_settings) {
            currentEntity.client.integration_details.integration_settings[key] = value;
        } else {
            let integrationDetails = {};
            integrationDetails[key] = value;
            currentEntity.client.integration_details.integration_settings = integrationDetails;
        }

        this.setState(prevState => ({
            integrationSettings: {
                ...prevState.integrationSettings,
                [key]: value
            }
        }));
    }

    handleSave = async () => {
        const { clientStore, commonStore, t } = this.props;
        const { integrationSettings, client_id, payrollProduct } = this.state;
        this.setState({ isSaving: true });
        try {
            const data = {
                integrationSettings: {
                    syncTimesheetForHourlyPaidEmp: integrationSettings.syncTimesheetForHourlyPaidEmp,
                    syncTimesheetForFixedPaidEmp: integrationSettings.syncTimesheetForFixedPaidEmp,
                    syncTimesheetSupplementsForHourlyPaidEmp: integrationSettings.syncTimesheetSupplementsForHourlyPaidEmp,
                    syncTimesheetSupplementsForFixedPaidEmp: integrationSettings.syncTimesheetSupplementsForFixedPaidEmp,
                    syncAdditionalPaymentsWithAbsence: integrationSettings.syncAdditionalPaymentsWithAbsence,
                    sendAbsenceHoursInPercentage: payrollProduct === payrollIntegrationOptions.VISMA ? integrationSettings.sendAbsenceHoursInPercentage : false,
                    allowAutoImportEntity: integrationSettings.allowAutoImportEntity,
                    allowLockingSyncedData: integrationSettings.allowLockingSyncedData,
                    syncedDataLockPeriodInWeeks: integrationSettings.syncedDataLockPeriodInWeeks,
                    useGeneralActivitiesForTimesheet: payrollProduct === payrollIntegrationOptions.TRIPLETEX ? integrationSettings.useGeneralActivitiesForTimesheet : false
                }
            };

            const response = await clientStore.saveIntegrationSettings(client_id, data);
            await this.props.clientStore.load(client_id)
            await commonStore.setAppLoaded();
            if (this.props.onSaveSuccess) this.props.onSaveSuccess();
            commonStore.addNotification(t(response.message), null, 'success');
        } catch (error) {
            console.error(error);
            commonStore.addNotification(t('Error saving settings'), null, 'error');
        } finally {
            this.setState({ isSaving: false });
        }
    }

    render() {
        const { t } = this.props;
        const { integrationSettings, isSaving } = this.state;
        if (!this.props.clientStore.currentEntity?.client?.integration_details?.product) {
            return (
                <div className="mt-3 text-center">
                    <i className="fa fa-exclamation-triangle margin-right-10"></i>
                    <span className='text-center font-size-15 fw-bold'>
                        {t('Please configure your payroll integration details first to access payroll integration settings.')}</span>
                </div>
            );
        }

        const { packageName } = this.state;
        const tripletexPlanSupported = !packageName || Object.values(TRIPLETEX_PLANS).includes(packageName);
        return (
            <>
                <div className='small mt-1'><b>{this.props.t('Note')}-</b>&nbsp;{this.props.t('All previously synced data will remain the same.')}</div>
                {this.state.payrollProduct === payrollIntegrationOptions.TRIPLETEX && !tripletexPlanSupported &&
                    <div className="mt-2 mb-1 text-warning fw-bold">
                        <i className="fa fa-exclamation-triangle me-2" />
                        {t('Projects, tasks, and activities are not available with your current Tripletex plan')} (<b>{packageName}</b>). {t('Upgrade to the PRO or KOMPLETT plan to enable these features.')}
                    </div>
                }
                {this.state.payrollProduct === payrollIntegrationOptions.TRIPLETEX &&
                    <>
                        <Row>
                            <Col xs={6}>
                                <Row className='mt-2'>
                                    <label className="control-label font-size-15 mt-2 font-color-primary">{t('Timesheet Registration Settings')}</label>
                                </Row>
                                <hr />
                                <Row>
                                    <Col className='mt-1' xs={2}>
                                        <SwitchWidget
                                            value={integrationSettings.useGeneralActivitiesForTimesheet}
                                            onChange={checked => this.handleIntegrationChange('useGeneralActivitiesForTimesheet', checked)}
                                        />
                                    </Col>
                                    <Col xs={10}>
                                        <label className="control-label mb-0">{t('Use General Activities for Timesheet Registration')}
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip id="tooltip-general-activities">
                                                        {t("Enable this option to sync logged working hours to a General Activity instead of a project activity. Use this if your Tripletex account doesn't use projects.")}
                                                    </Tooltip>
                                                }
                                            >
                                                <i className="fa fa-info-circle text-muted ms-1 opacity-50" aria-hidden="true"></i>
                                            </OverlayTrigger>
                                        </label>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <hr />
                    </>
                }
                <Row>
                    <Col xs={6}>
                        <Row className='mt-2'>
                            <label className="control-label font-size-15 mt-2 font-color-primary">{t('Fixed Salary – Timesheet Sync Rules')}</label>
                        </Row>
                        <hr />
                        <Row>
                            <Col className="mt-1" xs={2}>
                                <SwitchWidget
                                    value={integrationSettings?.syncTimesheetForFixedPaidEmp}
                                    onChange={checked => {
                                        this.handleIntegrationChange('syncTimesheetForFixedPaidEmp', checked);
                                    }}
                                />
                            </Col>
                            <Col xs={10}>
                                <label className="control-label mb-0">{t('Sync Normal Hours for Fixed Salary Employees')}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip id="tooltip-fixed">
                                                {t("If enabled, normal hours for 'Fixed' payment type employees will be synced to payroll. If disabled, they will be skipped.")}
                                            </Tooltip>
                                        }
                                    >
                                        <i className="fa fa-info-circle text-muted ms-1 opacity-50" aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                        </Row>
                        <Row className='align-items-center'>
                            <Col className="mt-1" sm={2} xs={2}>
                                <SwitchWidget
                                    value={integrationSettings?.syncTimesheetSupplementsForFixedPaidEmp}
                                    onChange={checked => {
                                        this.handleIntegrationChange('syncTimesheetSupplementsForFixedPaidEmp', checked);
                                    }}
                                />
                            </Col>
                            <Col sm={10}>
                                <label className="control-label">{t('Sync overtime hours & additional pay for Fixed salary employees')}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip id="tooltip-fixed">
                                                {t("If enabled, overtime hours and additional pay for 'Fixed' payment type employees will be synced. If disabled, they will be skipped.")}
                                            </Tooltip>
                                        }
                                    >
                                        <i className="fa fa-info-circle text-muted ms-1 opacity-50" aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={6}>
                        <Row className='mt-2'>
                            <label className="control-label font-size-15 mt-2 font-color-primary">{t('Hourly Salary – Timesheet Sync Rules')}</label>
                        </Row>
                        <hr />
                        <Row>
                            <Col className='mt-1' xs={2}>
                                <SwitchWidget
                                    value={integrationSettings?.syncTimesheetForHourlyPaidEmp}
                                    onChange={checked => {
                                        this.handleIntegrationChange('syncTimesheetForHourlyPaidEmp', checked);
                                    }}
                                />
                            </Col>
                            <Col xs={10}>
                                <label className="control-label mb-0">{t('Sync Normal Hours for Hourly Salary Employees')}
                                    <OverlayTrigger
                                        placement="left"
                                        overlay={
                                            <Tooltip id="tooltip-hourly">
                                                {t("If enabled, normal hours for 'Hourly' payment type employees will be synced to payroll. If disabled, they will be skipped.")}
                                            </Tooltip>
                                        }
                                    >
                                        <i className="fa fa-info-circle text-muted ms-1 opacity-50" aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                        </Row>
                        <Row>
                            <Col className='mt-1' xs={2}>
                                <SwitchWidget
                                    value={integrationSettings?.syncTimesheetSupplementsForHourlyPaidEmp}
                                    onChange={checked => {
                                        this.handleIntegrationChange('syncTimesheetSupplementsForHourlyPaidEmp', checked);
                                    }}
                                />
                            </Col>
                            <Col xs={10}>
                                <label className="control-label mb-0">{t('Sync overtime hours & additional pay for Hourly salary employees.')}
                                    <OverlayTrigger
                                        placement="left"
                                        overlay={
                                            <Tooltip id="tooltip-hourly">
                                                {t("If enabled, overtime hours and additional pay for 'Hourly' payment type employees will be synced. If disabled, they will be skipped.")}
                                            </Tooltip>
                                        }
                                    >
                                        <i className="fa fa-info-circle text-muted ms-1 opacity-50" aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <hr />
                <Row>
                    <Col xs={6}>
                        <Row>
                            <label className='control-label font-size-15 mt-2 font-color-primary'>{t('Integration Settings')}</label>
                        </Row>
                        <hr />
                        <Row>
                            <Col className='mt-1' xs={2}>
                                <SwitchWidget
                                    value={integrationSettings.allowLockingSyncedData}
                                    onChange={checked => {
                                        this.handleIntegrationChange('allowLockingSyncedData', checked)
                                        if (!checked) {
                                            this.handleIntegrationChange('syncedDataLockPeriodInWeeks', null);
                                        } else {
                                            this.handleIntegrationChange('syncedDataLockPeriodInWeeks', this.syncDataLockPeriodOptions[0].value);
                                        }
                                    }}
                                />
                            </Col>
                            <Col xs={10}>
                                <label className='control-label mb-0'>{t('Enable Synced Data Locking')}
                                    <OverlayTrigger
                                        placement='right'
                                        overlay={
                                            <Tooltip id='tooltip-auto-import'>
                                                {t('Lock the synced data and prevent any further changes after defined period of time.')}
                                            </Tooltip>
                                        }
                                    >
                                        <i className='fa fa-info-circle text-muted ms-1 opacity-50' aria-hidden='true'></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                        </Row>
                        <Row>
                            <label className="control-label mt-2">{t('Synced Data Lock Period')}</label>
                            <Select
                                className="custom-select-max-height-110"
                                isDisabled={!integrationSettings.allowLockingSyncedData}
                                options={this.syncDataLockPeriodOptions}
                                value={this.syncDataLockPeriodOptions.find(item => item.value === integrationSettings?.syncedDataLockPeriodInWeeks)}
                                onChange={e => this.handleIntegrationChange('syncedDataLockPeriodInWeeks', e.value)}
                            />
                            <i className='fs12 d-block'>{t('After this period of time, the synced data will be locked and the system will prevent any further changes')}.</i>
                        </Row>
                    </Col>
                    <Col xs={6}>
                        <Row>
                            <label className='control-label font-size-15 mt-2 font-color-primary'>{t('Absence Sync Rules')}</label>
                        </Row>
                        <hr />
                        <Row>
                            <Col className='mt-1' xs={2}>
                                <SwitchWidget
                                    value={integrationSettings?.syncAdditionalPaymentsWithAbsence}
                                    onChange={checked => {
                                        this.handleIntegrationChange('syncAdditionalPaymentsWithAbsence', checked);
                                    }}
                                />
                            </Col>
                            <Col xs={10}>
                                <label className='control-label mb-0'>{t("Include Scheduled Shift Additional Hours in Absence Sync")}
                                    <OverlayTrigger
                                        placement='top'
                                        overlay={
                                            <Tooltip id='tooltip-absence-additional-sync'>
                                                {t('If enabled, additional hours from scheduled shifts will be included while syncing absences.')}
                                            </Tooltip>
                                        }
                                    >
                                        <i className='fa fa-info-circle text-muted ms-1 opacity-50' aria-hidden='true'></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                        </Row>
                        {this.state.payrollProduct === payrollIntegrationOptions.VISMA &&
                            <Row>
                                <Col className='mt-1' xs={2}>
                                    <SwitchWidget
                                        value={integrationSettings?.sendAbsenceHoursInPercentage}
                                        onChange={checked => {
                                            this.handleIntegrationChange('sendAbsenceHoursInPercentage', checked);
                                        }}
                                    />
                                </Col>
                                <Col xs={10}>
                                    <label className='control-label mb-0'>{t("Sync Absence Hours in Percentage")}
                                        <OverlayTrigger
                                            placement='top'
                                            overlay={
                                                <Tooltip id='tooltip-absence-additional-sync'>
                                                    {t('If enabled, absence hours will be synced in percentage.')}
                                                </Tooltip>
                                            }
                                        >
                                            <i className='fa fa-info-circle text-muted ms-1 opacity-50' aria-hidden='true'></i>
                                        </OverlayTrigger>
                                    </label>
                                </Col>
                            </Row>
                        }
                    </Col>
                    {this.state.payrollProduct === payrollIntegrationOptions.XLEDGER &&
                        <Col xs={6}>
                            <Row>
                                <label className='control-label font-size-15 mt-2 font-color-primary'>{t('Auto Import Rules')}</label>
                            </Row>
                            <hr />
                            <Row>
                                <Col className='mt-1' xs={2}>
                                    <SwitchWidget
                                        value={integrationSettings.allowAutoImportEntity}
                                        onChange={checked => this.handleIntegrationChange('allowAutoImportEntity', checked)}
                                    />
                                </Col>
                                <Col xs={10}>
                                    <label className='control-label mb-0'>{t('Auto Import Projects & Tasks')}
                                        <OverlayTrigger
                                            placement='left'
                                            overlay={
                                                <Tooltip id='tooltip-auto-import'>
                                                    {t('If enabled, new projects and their associated tasks will be automatically imported from Xledger daily.')}
                                                </Tooltip>
                                            }
                                        >
                                            <i className='fa fa-info-circle text-muted ms-1 opacity-50' aria-hidden='true'></i>
                                        </OverlayTrigger>
                                    </label>
                                </Col>
                            </Row>
                        </Col>
                    }
                </Row>
                <Row>
                    <Col className='d-flex justify-content-start mt-3'>
                        <Button
                            onClick={this.handleSave}
                            fill
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <i className="fa fa-spinner fa-spin fa-fw"></i>
                                    &nbsp;{t('Saving...')}
                                </>
                            ) : (
                                <>
                                    {t('Save')} {t('Settings')}
                                </>
                            )}
                        </Button>
                    </Col>
                </Row>
            </>
        );
    }
}

export default inject('commonStore', 'clientStore')(withLocalization(observer(IntegrationSettings)));